import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import { tableColumnWidths } from '@/features/shared/components/tableSystem/tableColumnWidths';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from '@/features/shared/components/tableSystem/TableLatestCellStyles';
import { TableStatusMetaContent } from '@/features/shared/components/tableSystem/TableMetaCells';
import { resolveEntityTags, resolveTagColor } from '@/features/shared/tags';
import {
  formatUploadStatusLabel,
  getUploadStatusChipColor,
  getUploadStatusIcon,
} from '@/features/uploads/formatters';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { Box } from '@/ui';
import { TableNameCell } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { EventOutlinedIcon, PersonOutlineOutlinedIcon } from '@/ui/primitives/icons';
import { UploadErrorTableCell } from './UploadErrorTableCell';
import type { TagColor } from '@/shared/data/tags';
import type { GridColDef, GridRenderCellParams } from '@/ui/primitives';
import type { UploadDto } from '@flowqa/shared';

const columnWidths = tableColumnWidths.uploads;

const resolveUploadStatusTone = (
  status: UploadDto['status'],
): 'green' | 'red' | 'orange' | 'brown' | 'blue' | 'purple' | 'neutral' => {
  const color = getUploadStatusChipColor(status);
  if (color === 'green') return 'green';
  if (color === 'red') return 'red';
  if (color === 'yellow') return 'orange';
  if (color === 'brown') return 'brown';
  if (color === 'blue') return 'blue';
  if (color === 'purple') return 'purple';
  return 'neutral';
};

export const buildUploadsColumns = ({
  editRowPendingId = null,
  deleteRowPendingId = null,
  restoreRowPendingId = null,
  onEditRow,
  onDeleteRow,
  onRestoreRow,
  tagColorByName = {},
  updateRowTagsColorPendingId = null,
}: {
  editRowPendingId?: string | null;
  deleteRowPendingId?: string | null;
  restoreRowPendingId?: string | null;
  onEditRow?: (row: UploadDto) => void;
  onDeleteRow?: (row: UploadDto) => void;
  onRestoreRow?: (row: UploadDto) => void;
  tagColorByName?: Partial<Record<string, TagColor>>;
  updateRowTagsColorPendingId?: string | null;
} = {}): Array<GridColDef<UploadDto>> => [
  {
    field: 'id',
    headerName: 'Upload',
    flex: columnWidths.id.flex,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    cellClassName: 'table-name-cell',
    renderCell: (params) => {
      const tags = resolveEntityTags(params.row);
      const fileLink = params.row.resultsFileSlug
        ? `/results-files/${params.row.resultsFileSlug}`
        : null;
      const resolvedTags = tags.map((tag) => ({
        label: tag,
        tone: resolveTagColor(tag, tagColorByName),
      }));
      const displayTags = params.row.deletedAt
        ? [{ label: 'Deleted', tone: 'red' as const }, ...resolvedTags]
        : resolvedTags;

      return (
        <TableCellContainer gap="none">
          <TableNameCell
            primary={{
              label: params.row.id,
              href: `/uploads/${params.row.id}`,
            }}
            secondary={
              fileLink
                ? {
                    label: params.row.fileName,
                    href: fileLink,
                    role: 'caption',
                  }
                : {
                    label: params.row.fileName,
                    role: 'caption',
                  }
            }
            tags={displayTags}
          />
        </TableCellContainer>
      );
    },
  },
  {
    field: 'status',
    headerName: getDetailLabel('status'),
    flex: columnWidths.status.flex,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    cellClassName: 'table-status-meta-cell',
    renderCell: (params) => {
      const tone = resolveUploadStatusTone(params.row.status);
      const uploadStatusIcon = getUploadStatusIcon(params.row.status);
      return (
        <TableCellContainer gap="none">
          <TableStatusMetaContent
            statusLabel={formatUploadStatusLabel(params.row.status)}
            statusHref={`/uploads/${params.row.id}`}
            onStatusClick={(event) => {
              event.stopPropagation();
            }}
            chipTone={tone}
            chipIcon={<Box component={uploadStatusIcon} />}
            chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, tone)}
            chipForegroundColor={(theme) => resolveLatestChipForeground(theme, tone)}
            durationLabel={formatRelativeTime(params.row.createdAt) || '-'}
            durationTitle="Uploaded Timestamp"
            durationValueTitle={formatDate(params.row.createdAt, { includeTime: true }) || ''}
            durationIcon={
              <EventOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
            }
            timestampLabel={params.row.createdByName ?? params.row.createdByEmail ?? '-'}
            timestampTitle={null}
            timestampIcon={
              <PersonOutlineOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
            }
            timestampRowTitle="Uploaded By"
          />
        </TableCellContainer>
      );
    },
  },
  {
    field: 'error',
    headerName: 'Error',
    flex: columnWidths.error.flex,
    filterable: false,
    sortable: false,
    cellClassName: 'table-meta-details-cell',
    renderCell: (params) => {
      const errorMessage = params.row.errorSummary ?? params.row.error ?? null;
      return <UploadErrorTableCell error={errorMessage} />;
    },
  },
  {
    field: 'actions',
    headerName: '',
    flex: columnWidths.actions.flex,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: GridRenderCellParams<UploadDto>) => (
      <TableActionsCell
        rowName={params.row.fileName}
        editActionLabel={getEntityActionLabel('edit', 'Upload')}
        deleteActionLabel={getEntityActionLabel('delete', 'Upload')}
        onEdit={() => {
          onEditRow?.(params.row);
        }}
        onDelete={
          params.row.deletedAt
            ? undefined
            : () => {
                onDeleteRow?.(params.row);
              }
        }
        onRestore={
          params.row.deletedAt
            ? () => {
                onRestoreRow?.(params.row);
              }
            : undefined
        }
        disableEdit={
          editRowPendingId === params.row.id ||
          deleteRowPendingId === params.row.id ||
          restoreRowPendingId === params.row.id ||
          updateRowTagsColorPendingId === params.row.id
        }
        disableDelete={deleteRowPendingId === params.row.id}
        disableRestore={restoreRowPendingId === params.row.id}
        restoreActionLabel={getEntityActionLabel('restore', 'Upload')}
      />
    ),
  },
];
